import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Award,
  BarChart2,
  Bell,
  Briefcase,
  Building2,
  CalendarCheck,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileText,
  Kanban,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Scale,
  Settings2,
  Sparkles,
  Target,
  TrendingUp,
  UserRound,
  UserSearch,
  Users,
  Wallet,
  X,
} from "lucide-react";
import {
  Link,
  NavLink,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import NotificationsDrawer from "@/components/admin/recruitment/NotificationsDrawer";
import { listInternalNotifications } from "@/lib/recruitmentApi";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/context/AuthContext";
import {
  getBreadcrumbItems,
  getNavigationMatch,
  getNavigationSectionsForRole,
  resolveNavigationContext,
  resolveNavigationPath,
} from "@/lib/navigation";
import { getLogoTarget } from "@/lib/logoRouting";
import { TESTIDS } from "@/lib/testids";

const ICON_BY_MATCHER = [
  { matcher: "/dashboard", icon: LayoutDashboard },
  { matcher: "/besoins", icon: ClipboardList },
  { matcher: "/candidats", icon: UserSearch },
  { matcher: "/vivier", icon: Award },
  { matcher: "tab=public", icon: Users },
  { matcher: "/candidatures", icon: Kanban },
  { matcher: "/entretiens", icon: CalendarCheck },
  { matcher: "/communications", icon: Mail },
  { matcher: "/pipeline", icon: Kanban },
  { matcher: "/collaborateurs", icon: Users },
  { matcher: "/clients", icon: Building2 },
  { matcher: "/missions", icon: Briefcase },
  { matcher: "/charges-fixes", icon: Wallet },
  { matcher: "/rapprochement-financier", icon: Scale },
  { matcher: "/frais-collaborateurs", icon: Wallet },
  { matcher: "/objectifs", icon: Target },
  { matcher: "/simulation", icon: TrendingUp },
  { matcher: "/statistiques", icon: BarChart2 },
  { matcher: "/offres", icon: Briefcase },
  { matcher: "/cv-library", icon: FileText },
  { matcher: "/cv-builder", icon: Sparkles },
  { matcher: "/settings", icon: Settings2 },
  { matcher: "/collab", icon: UserRound },
];

const CONTEXT_LABELS = {
  pilotage: "Pilotage",
  carrieres: "Carrières & Candidatures",
  postulation: "Carrières & Candidatures",
  recrutement: "Recrutement Intelligent",
  outils: "Outils",
  collab: "Mon espace",
  administration: "Administration",
};

const SIDEBAR_WIDTH = "200px";
const SIDEBAR_COLLAPSED_WIDTH = "72px";
const SIDEBAR_COLLAPSED_STORAGE_KEY = "lenvia_admin_sidebar_collapsed";
const SIDEBAR_LOGO_SRC = "/lenvia-logo.png";
const SIDEBAR_COLLAPSED_ICON_SRC = "/lenvia-sidebar-collapsed-icon.png";

const getItemIcon = (path) => {
  const matchedIcon = ICON_BY_MATCHER.find((entry) => path.includes(entry.matcher));
  return matchedIcon?.icon || Sparkles;
};

const isActivePath = (currentLocation, expectedPath) => {
  const currentPathname = typeof currentLocation === "string" ? currentLocation : currentLocation.pathname;
  const currentSearch = typeof currentLocation === "string" ? "" : (currentLocation.search || "");

  if (expectedPath.includes("?")) {
    const [expectedBase, expectedQuery] = expectedPath.split("?");
    if (currentPathname !== expectedBase && !currentPathname.startsWith(`${expectedBase}/`)) {
      return false;
    }
    const expectedParams = new URLSearchParams(expectedQuery);
    const currentParams = new URLSearchParams(currentSearch);
    for (const [k, v] of expectedParams.entries()) {
      if (currentParams.get(k) !== v) return false;
    }
    return true;
  }

  if (expectedPath === "/admin/recrutement/candidatures" && currentSearch.includes("tab=public")) {
    return false;
  }

  return currentPathname === expectedPath || currentPathname.startsWith(`${expectedPath}/`);
};

const isSectionActive = (currentLocation, section) => {
  if (section.items.some((item) => isActivePath(currentLocation, item.path))) {
    return true;
  }

  const currentPathname = typeof currentLocation === "string" ? currentLocation : currentLocation.pathname;
  return currentPathname === section.basePath || currentPathname.startsWith(`${section.basePath}/`);
};

const getStoredSidebarCollapsed = () => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const AppShell = () => {
  const { authInitialized, isAuthenticated, role, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(getStoredSidebarCollapsed);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const normalizedPath = resolveNavigationPath(location.pathname);
  const navSections = useMemo(
    () => {
      const sections = getNavigationSectionsForRole(role, "primary");
      const profiles = user?.recruitment_profiles || [];
      if (profiles.some(profile => ['rh','cto','cco'].includes(profile)) && !sections.some(section => section.section === 'RECRUTEMENT')) {
        return [...getNavigationSectionsForRole('RH','primary').filter(section => section.section === 'RECRUTEMENT').map(section => ({...section,items:section.items.filter(item => item.label !== 'CVs')})),...sections];
      }
      return sections;
    },
    [role,user],
  );
  const footerSections = useMemo(
    () => getNavigationSectionsForRole(role, "footer"),
    [role],
  );
  const activeMatch = getNavigationMatch(normalizedPath);
  const breadcrumbs = getBreadcrumbItems(normalizedPath);
  const context = resolveNavigationContext(normalizedPath);
  const logoTarget = getLogoTarget({ role, isAuthenticated });
  const compactSidebar = sidebarCollapsed && !sidebarOpen;
  const sidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  useEffect(() => {
    document.body.classList.add("admin-shell");

    return () => {
      document.body.classList.remove("admin-shell");
    };
  }, []);

  const fetchUnreadNotifications = useCallback(async () => {
    if (process.env.NODE_ENV === "test") return;
    if (role === "ADMIN" || role === "RH" || role === "SUPER_ADMIN") {
      try {
        const notifs = await listInternalNotifications(false);
        setUnreadNotifCount(Array.isArray(notifs) ? notifs.length : 0);
      } catch {
        // Silent fail
      }
    }
  }, [role]);

  useEffect(() => {
    void fetchUnreadNotifications();
  }, [fetchUnreadNotifications, normalizedPath]);

  useEffect(() => {
    setSidebarOpen(false);
    contentRef.current?.scrollTo?.({ top: 0, left: 0 });
  }, [normalizedPath]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SIDEBAR_COLLAPSED_STORAGE_KEY,
        sidebarCollapsed ? "true" : "false",
      );
    } catch {
      // Ignore localStorage failures and keep a transient UI state.
    }
  }, [sidebarCollapsed]);

  const currentNavLocation = useMemo(
    () => ({ pathname: normalizedPath, search: location.search || "" }),
    [normalizedPath, location.search],
  );

  useEffect(() => {
    // Keep active section open
    navSections.concat(footerSections).forEach((section) => {
      if (isSectionActive(currentNavLocation, section)) {
        setCollapsedSections((prev) => ({ ...prev, [section.section]: false }));
      }
    });
  }, [currentNavLocation, navSections, footerSections]);

  if (!authInitialized) {
    return (
      <div className="min-h-screen bg-lenvia-bg flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-lenvia-blue border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const renderNavigationSection = (section, isCompact = false) => {
    const sectionActive = isSectionActive(currentNavLocation, section);
    const isCollapsed = collapsedSections[section.section];

    return (
      <section className="space-y-0.5">
        {isCompact ? null : (
          <button
            type="button"
            onClick={() =>
              setCollapsedSections((prev) => ({
                ...prev,
                [section.section]: !prev[section.section],
              }))
            }
            className="flex min-h-8 w-full items-center justify-between rounded-md px-2.5 pb-0.5"
            aria-expanded={!isCollapsed}
          >
            <div className="flex items-center gap-1.5">
              <span
                className={`h-1 w-1 rounded-full transition-colors ${sectionActive ? "bg-lenvia-blue" : "bg-slate-600/90"
                  }`}
              />
              <p
                className={`text-xs font-semibold uppercase tracking-[0.1em] transition-colors ${sectionActive ? "text-lenvia-blue/95" : "text-slate-400/90"
                  }`}
              >
                {section.section}
              </p>
            </div>
            {isCollapsed ? (
              <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
            )}
          </button>
        )}

        <div
          className={`space-y-0.5 overflow-hidden transition-all duration-200 ease-in-out ${
            !isCompact && isCollapsed ? "max-h-0 opacity-0" : "max-h-[500px] opacity-100"
          }`}
        >
          {section.items.map((item) => {
            const Icon = getItemIcon(item.path);
            const itemActive = isActivePath(currentNavLocation, item.path);
            const itemLink = (
              <NavLink
                key={item.path}
                to={item.path}
                aria-label={isCompact ? item.label : undefined}
                className={`group relative flex h-10 items-center rounded-lg border text-[13px] font-medium transition-colors duration-200 ${isCompact ? "justify-center px-0" : "gap-2.5 px-2.5"
                  } ${itemActive
                    ? "border-blue-400/25 bg-gradient-to-r from-blue-500/14 to-blue-500/[0.03] text-blue-50 shadow-[inset_2px_0_0_rgba(96,165,250,0.9)]"
                    : "border-transparent text-slate-300/90 hover:border-white/10 hover:bg-white/[0.04] hover:text-white"
                  }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${itemActive ? "bg-blue-500/20 text-blue-100" : "text-slate-400 group-hover:text-slate-200"
                    }`}
                >
                  <Icon className="h-4 w-4" />
                </span>
                {isCompact ? null : (
                  <span className="min-w-0 truncate text-[13px] leading-5">{item.label}</span>
                )}
              </NavLink>
            );

            if (!isCompact) {
              return itemLink;
            }

            return (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>{itemLink}</TooltipTrigger>
                <TooltipContent
                  side="right"
                  className="rounded-lg border border-white/10 bg-slate-900 px-2 py-1 text-[11px] text-slate-100"
                >
                  {item.label}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </section>
    );
  };

  const logoutButton = (
    <Button
      type="button"
      variant="outline"
      onClick={handleLogout}
      data-testid={TESTIDS.shell.logout}
      aria-label={compactSidebar ? "Déconnexion" : undefined}
      className={`mt-2 h-[34px] w-full border-white/10 bg-white/[0.02] text-[12px] font-medium text-slate-300 hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-200 ${compactSidebar ? "justify-center px-0" : "justify-start px-2.5"
        }`}
    >
      <LogOut className={`h-4 w-4 ${compactSidebar ? "mr-0" : "mr-2"}`} />
      {compactSidebar ? null : "Déconnexion"}
    </Button>
  );

  return (
    <div
      className="relative grid h-screen min-h-screen grid-cols-1 bg-lenvia-bg lg:grid-cols-[var(--sidebar-width)_minmax(0,1fr)]"
      style={{ "--sidebar-width": sidebarWidth }}
    >
      {sidebarOpen ? (
        <button
          type="button"
          aria-label="Fermer le menu"
          className="absolute inset-0 z-40 bg-black/70 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[200px] max-w-[85vw] -translate-x-full transition-transform duration-200 lg:sticky lg:top-0 lg:h-screen lg:w-[var(--sidebar-width)] lg:max-w-none lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : ""
          }`}
      >
        <TooltipProvider delayDuration={120}>
          <div className="flex h-full flex-col overflow-hidden border-r border-white/10 bg-[linear-gradient(180deg,rgba(15,23,42,0.96)_0%,rgba(2,6,23,0.98)_100%)] backdrop-blur-lg">
            <div className={`flex h-12 items-center justify-between border-b border-white/10 ${compactSidebar ? "px-2" : "px-3"}`}>
              <Link
                to={logoTarget}
                aria-label="Lenvia"
                data-testid={TESTIDS.shell.logo}
                className={`flex min-w-0 items-center ${compactSidebar ? "w-full justify-center" : "gap-2"}`}
              >
                {compactSidebar ? (
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl border border-blue-400/25 bg-gradient-to-b from-blue-400/20 to-blue-500/5 shadow-[0_6px_20px_rgba(14,165,233,0.18)]">
                    <img src={SIDEBAR_COLLAPSED_ICON_SRC} alt="Lenvia" className="h-6 w-6 object-contain" />
                  </span>
                ) : (
                  <>
                    <img src={SIDEBAR_LOGO_SRC} alt="Lenvia" className="h-6 w-auto" />
                    <span className="truncate text-[9px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                      {role === "COLLAB" ? "Espace" : "Admin"}
                    </span>
                  </>
                )}
              </Link>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid={TESTIDS.shell.sidebarToggle}
                  aria-label={sidebarCollapsed ? "Agrandir la sidebar" : "Réduire la sidebar"}
                  className="hidden rounded-md p-1.5 text-slate-500 transition-colors hover:bg-white/[0.05] hover:text-slate-100 lg:inline-flex"
                  onClick={() => setSidebarCollapsed((previousState) => !previousState)}
                >
                  {sidebarCollapsed ? (
                    <ChevronsRight className="h-4 w-4" />
                  ) : (
                    <ChevronsLeft className="h-4 w-4" />
                  )}
                </button>
                <button
                  type="button"
                  className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-white/5 hover:text-white lg:hidden"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {compactSidebar ? null : (
              <div className="border-b border-white/10 px-3 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500/90">
                  Contexte Actif
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-500/10 text-blue-400">
                    <Sparkles className="h-3.5 w-3.5" />
                  </div>
                  <p className="text-[13px] font-semibold text-slate-100">
                    {CONTEXT_LABELS[context] || "Administration"}
                  </p>
                </div>
              </div>
            )}

            <nav className={`flex-1 overflow-y-auto py-2.5 ${compactSidebar ? "px-2" : "px-2.5"}`}>
              <div className="space-y-2.5">
                {navSections.map((section, index) => (
                  <div key={`${section.section}-${section.basePath}`}>
                    {index > 0 ? <div className="mb-2.5 h-px bg-white/[0.06]" /> : null}
                    {renderNavigationSection(section, compactSidebar)}
                  </div>
                ))}
              </div>
            </nav>

            <div className={`border-t border-white/10 ${compactSidebar ? "p-2" : "p-2"}`}>
              {footerSections.length > 0 ? (
                <div className="mb-2 space-y-2.5">
                  {footerSections.map((section, index) => (
                    <div key={`${section.section}-${section.basePath}`}>
                      {index > 0 ? <div className="mb-2.5 h-px bg-white/[0.06]" /> : null}
                      {renderNavigationSection(section, compactSidebar)}
                    </div>
                  ))}
                </div>
              ) : null}

              {compactSidebar ? null : (
                <div className="rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-2">
                  <p className="truncate text-[12px] font-semibold text-slate-100">{user?.name || "Utilisateur"}</p>
                  <p className="mt-0.5 truncate text-[11px] text-slate-400">{user?.email}</p>
                  <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    {role}
                  </p>
                </div>
              )}
              {compactSidebar ? (
                <Tooltip>
                  <TooltipTrigger asChild>{logoutButton}</TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="rounded-lg border border-white/10 bg-slate-900 px-2 py-1 text-[11px] text-slate-100"
                  >
                    Déconnexion
                  </TooltipContent>
                </Tooltip>
              ) : (
                logoutButton
              )}
            </div>
          </div>
        </TooltipProvider>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-col">
        <header className="flex h-16 shrink-0 items-center justify-between gap-4 border-b border-white/10 bg-lenvia-bg/90 px-4 backdrop-blur-sm sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-white/5 hover:text-white lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>

            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                {CONTEXT_LABELS[context] || "Administration"}
              </p>
              <div className="mt-1 flex min-w-0 items-center gap-2 overflow-hidden text-sm">
                {breadcrumbs.length > 0 ? (
                  breadcrumbs.map((item, index) => (
                    <div key={`${item.label}-${index}`} className="flex min-w-0 items-center gap-2">
                      {index > 0 ? (
                        <ChevronRight className="h-4 w-4 shrink-0 text-slate-600" />
                      ) : null}
                      {item.path && index < breadcrumbs.length - 1 ? (
                        <NavLink
                          to={item.path}
                          className="truncate text-slate-400 transition-colors hover:text-white"
                        >
                          {item.label}
                        </NavLink>
                      ) : (
                        <span className="truncate text-white">{item.label}</span>
                      )}
                    </div>
                  ))
                ) : (
                  <span className="text-white">{activeMatch?.section?.section || "Tableau de bord"}</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {(role === "ADMIN" || role === "RH" || role === "SUPER_ADMIN") && (
              <button
                type="button"
                onClick={() => setNotificationsOpen(true)}
                className="relative rounded-lg p-2 text-slate-400 transition-colors hover:bg-white/5 hover:text-white"
                aria-label="Notifications"
                data-testid="recruitment-header-notifications-button"
              >
                <Bell className="h-5 w-5" />
                {unreadNotifCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-blue-500 px-1 text-[10px] font-bold text-white shadow-xs"
                    data-testid="recruitment-header-unread-badge"
                  >
                    {unreadNotifCount > 99 ? "99+" : unreadNotifCount}
                  </span>
                )}
              </button>
            )}

            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 text-sm text-slate-400 transition-colors hover:text-lenvia-blue"
            >
              Voir le site
            </a>
          </div>
        </header>

        <main
          ref={contentRef}
          className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 sm:px-6 lg:px-8"
        >
          <div className="mx-auto w-full max-w-7xl">
            <Outlet />
          </div>
        </main>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "#0f172a",
              color: "#f8fafc",
              border: "1px solid rgba(148, 163, 184, 0.3)",
            },
          }}
        />
        <NotificationsDrawer
          isOpen={notificationsOpen}
          onClose={() => {
            setNotificationsOpen(false);
            void fetchUnreadNotifications();
          }}
          onSelectNotification={(notif) => {
            setNotificationsOpen(false);
            if (notif?.metadata?.need_id) {
              navigate(`/admin/recrutement/besoins/${notif.metadata.need_id}`);
            } else if (notif?.metadata?.candidate_id) {
              navigate(`/admin/recrutement/candidats/${notif.metadata.candidate_id}`);
            } else if (notif?.metadata?.application_id) {
              navigate(`/admin/recrutement/candidatures?application_id=${notif.metadata.application_id}`);
            } else {
              navigate("/admin/recrutement/candidatures");
            }
          }}
          onSelectApplication={(appId) => {
            setNotificationsOpen(false);
            if (appId) {
              navigate(`/admin/recrutement/candidatures?application_id=${appId}`);
            } else {
              navigate("/admin/recrutement/candidatures");
            }
          }}
        />
      </div>
    </div>
  );
};

export default AppShell;
